import { useState, useEffect, useCallback } from 'react';
import { useCommonPageContext } from '@atlassian/jira-servicedesk-common-page-context';
import {
	COLLAPSED_MODE,
	OTHER_PROJECT_CALENDARS_EDIT_MODE,
	VIEW_MODE,
} from '../../../common/constants';
import type { OtherProjectCalendarMode } from '../../../common/types';
import { useSLAConfigActionsFlags } from '../../../common/ui/sla-config-action-flags';
import type { OtherProjectCalendar } from '../../../common/utils/calendars/types';
import type { OtherProjectCalendarsModalStateAndActions } from '../../../common/utils/reusable-calendars/types';
import { useWorkingHoursServiceOtherProjectCalendars } from '../../../services/working-hours-other-calendars/main.tsx';

export const useCalendarsModalStateAndActionsOtherCalendars = (
	isCalendarModalOpen: boolean,
	isOtherCalendarsTabSelected: () => boolean,
): OtherProjectCalendarsModalStateAndActions => {
	const { baseUrl, projectKey } = useCommonPageContext();
	const [selectedCalendarId, setSelectedCalendarId] = useState<unknown>();
	const [selectedCalendarMode, setSelectedCalendarMode] =
		useState<OtherProjectCalendarMode>(VIEW_MODE);
	const [calendars, setCalendars] = useState<unknown>();
	const [{ data, loading, error }, { fetch }] = useWorkingHoursServiceOtherProjectCalendars(
		// @ts-expect-error(TS2322 baseUrl, projectId or projectKey can be undefined, if usePageContext() returns null. Please handle undefined value in your code) - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
		baseUrl,
		projectKey,
	);
	const [permissionsWithTimezoneInfo, setPermissionsWithTimezoneInfo] = useState<unknown>();
	const { calendarSaveSuccessFlag, calendarDeleteSuccessFlag } = useSLAConfigActionsFlags();

	useEffect(() => {
		const fetchData = async () => {
			if (isCalendarModalOpen && isOtherCalendarsTabSelected() && !data && !error) {
				await fetch();
			}
		};
		fetchData();
	}, [data, error, fetch, isCalendarModalOpen, isOtherCalendarsTabSelected, loading]);

	useEffect(() => {
		if (data) {
			const { calendars: calendarData, ...permissionsAndTimezoneData } = data;
			setCalendars(calendarData);
			setPermissionsWithTimezoneInfo(permissionsAndTimezoneData);
		}
	}, [data]);

	const onToggleViewRequestedHelper = useCallback(
		(calendarId?: number) => {
			const shouldCollapse =
				selectedCalendarId === calendarId && selectedCalendarMode !== COLLAPSED_MODE;
			if (shouldCollapse) {
				setSelectedCalendarId(undefined);
				setSelectedCalendarMode(COLLAPSED_MODE);
			} else {
				setSelectedCalendarId(calendarId);
				setSelectedCalendarMode(VIEW_MODE);
			}
		},
		[selectedCalendarId, selectedCalendarMode],
	);

	const onToggleViewRequested = useCallback(
		(calendarId?: number) => {
			onToggleViewRequestedHelper(calendarId);
		},
		[onToggleViewRequestedHelper],
	);

	const onEditRequestedHelper = useCallback((calendarId: number) => {
		setSelectedCalendarId(calendarId);
		setSelectedCalendarMode(OTHER_PROJECT_CALENDARS_EDIT_MODE);
	}, []);

	const onEditRequested = useCallback(
		(calendarId: number) => {
			onEditRequestedHelper(calendarId);
		},
		[onEditRequestedHelper],
	);

	// onEditCalendarSuccessful is invoked after modifyCalendar Async is successful
	const onEditCalendarSuccessful = useCallback(
		(modifiedCalendar: OtherProjectCalendar) => {
			// @ts-expect-error - TS2488 - Type 'unknown' must have a '[Symbol.iterator]()' method that returns an iterator.
			const updatedCalendarList = [...(calendars || [])];
			const modifiedIndex = updatedCalendarList.findIndex(({ id }) => id === modifiedCalendar.id);
			if (modifiedIndex === -1) {
				updatedCalendarList.push(modifiedCalendar);
			} else {
				updatedCalendarList.splice(modifiedIndex, 1, modifiedCalendar);
			}
			setSelectedCalendarMode(VIEW_MODE);
			setCalendars(updatedCalendarList);
			calendarSaveSuccessFlag(modifiedCalendar?.name, false); // Since calendars of other projects can only be edited
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[calendars],
	);

	// onReset reset the state to defaults
	const onReset = useCallback(() => {
		setSelectedCalendarId(undefined);
		setSelectedCalendarMode(VIEW_MODE);
	}, []);

	// onDeleteCalendarSuccessful is invoked after deleteCalendar Async is successful
	const onDeleteCalendarSuccessful = useCallback(
		(calendarID: number) => {
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			const deletedCalendar = calendars?.find(
				(calendar: OtherProjectCalendar) => calendar.id === calendarID,
			);
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			const updatedCalendarList = calendars?.filter(
				(calendar: OtherProjectCalendar) => calendarID !== calendar.id,
			);
			setCalendars(updatedCalendarList);
			deletedCalendar && calendarDeleteSuccessFlag(deletedCalendar.name);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[calendars],
	);

	const state = {
		selectedCalendarId,
		selectedCalendarMode,
		calendars,
		isLoading: loading,
		isError: Boolean(error),
		permissionsWithTimezoneInfo,
	};

	const actions = {
		onToggleViewRequested,
		onReset,
		onEditRequested,
		onEditCalendarSuccessful,
		onDeleteCalendarSuccessful,
	};

	// @ts-expect-error - TS2322 - Type '{ selectedCalendarId: unknown; selectedCalendarMode: OtherProjectCalendarMode; calendars: unknown; isLoading: boolean; isError: boolean; permissionsWithTimezoneInfo: unknown; }' is not assignable to type 'OtherProjectCalendarsModalState'.
	return [state, actions];
};
